import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from "react";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  AlertCircle,
  CheckCircle2,
  ExternalLink,
  Eye,
  Loader2,
  Lock,
  Plus,
  Store,
} from "lucide-react";
import type {
  StorefrontBranding,
  StorefrontItem,
  StorefrontLayout,
} from "@lucralink/shared";
import {
  DEFAULT_STOREFRONT_ACCENT,
  DEFAULT_STOREFRONT_LAYOUT,
  STOREFRONT_LAYOUTS,
} from "@lucralink/shared";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { StorefrontItemRow } from "@/components/vitrine/StorefrontItemRow";
import { StorefrontPreview } from "@/components/vitrine/StorefrontPreview";
import { VitrineSettingsCard } from "@/components/vitrine/VitrineSettingsCard";
import { useLinks } from "@/lib/hooks/useLinks";
import {
  storefrontErrorLabel,
  useDeleteStorefrontItem,
  useReorderStorefrontItems,
  useSetTenantStorefront,
  useStorefront,
  useStorefrontItems,
  useUpsertStorefrontItem,
} from "@/lib/hooks/useStorefront";
import { marketplaceLabel } from "@/lib/marketplace";
import { cn } from "@/lib/utils";

// Base do subdominio publico da vitrine (Worker Cloudflare, fora da Vercel).
const STOREFRONT_PUBLIC_BASE = "https://loja.lucralink.com.br";

type NoticeTone = "neutral" | "success" | "warning" | "danger";

interface Notice {
  tone: NoticeTone;
  message: string;
}

export function VitrinePage(): JSX.Element {
  const { data: storefront, isLoading: loadingStorefront, error: storefrontError } = useStorefront();
  const { data: items = [], isLoading: loadingItems } = useStorefrontItems();
  const { data: links = [] } = useLinks();

  const setStorefront = useSetTenantStorefront();
  const upsertItem = useUpsertStorefrontItem();
  const deleteItem = useDeleteStorefrontItem();
  const reorderItems = useReorderStorefrontItems();

  // Estado do form de identidade da vitrine.
  const [slug, setSlug] = useState("");
  const [storeName, setStoreName] = useState("");
  const [bio, setBio] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [accentColor, setAccentColor] = useState(DEFAULT_STOREFRONT_ACCENT);
  const [layout, setLayout] = useState<StorefrontLayout>(DEFAULT_STOREFRONT_LAYOUT);

  // Estado do form de novo item.
  const [linkId, setLinkId] = useState("");
  const [itemTitle, setItemTitle] = useState("");
  const [itemImageUrl, setItemImageUrl] = useState("");
  const [itemPrice, setItemPrice] = useState("");

  // Ordenacao local (otimista) para o drag-and-drop.
  const [orderedIds, setOrderedIds] = useState<string[]>([]);

  const [brandingNotice, setBrandingNotice] = useState<Notice | null>(null);
  const [itemNotice, setItemNotice] = useState<Notice | null>(null);
  const [busyItemId, setBusyItemId] = useState<string | null>(null);

  // pro e pro_ai tem hasShowcase (ver packages/shared/src/entitlements.ts e a
  // migration 20260714130000_fix_set_tenant_storefront_pro_ai.sql).
  // storefront.isPro ja resolve plan + pro_until (resolveEntitlements) - NAO
  // comparar storefront.plan !== "free" cru, que ignora assinatura vencida.
  const isPro = storefront != null && storefront.isPro;
  const isPublished = storefront?.status === "published";

  // Hidrata o form quando o tenant carrega.
  useEffect(() => {
    if (!storefront) return;
    const branding = storefront.branding;
    setSlug(storefront.slug ?? "");
    setStoreName(branding.storeName ?? "");
    setBio(branding.bio ?? "");
    setLogoUrl(branding.logoUrl ?? "");
    setAccentColor(branding.accentColor ?? DEFAULT_STOREFRONT_ACCENT);
    setLayout(branding.layout ?? DEFAULT_STOREFRONT_LAYOUT);
  }, [storefront]);

  // Mantem a ordem local em sincronia com os itens do servidor.
  useEffect(() => {
    setOrderedIds(items.map((item) => item.id));
  }, [items]);

  const itemsById = useMemo(
    () => new Map(items.map((item) => [item.id, item])),
    [items],
  );
  const orderedItems = useMemo(
    () =>
      orderedIds
        .map((id) => itemsById.get(id))
        .filter((item): item is StorefrontItem => Boolean(item)),
    [orderedIds, itemsById],
  );

  const currentBranding: StorefrontBranding = useMemo(
    () => buildBranding({ storeName, bio, logoUrl, accentColor, layout }),
    [storeName, bio, logoUrl, accentColor, layout],
  );

  const usedLinkIds = useMemo(() => new Set(items.map((item) => item.linkId)), [items]);
  const availableLinks = useMemo(
    () => links.filter((link) => !usedLinkIds.has(link.id)),
    [links, usedLinkIds],
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleSaveBranding = async (
    event: FormEvent<HTMLFormElement>,
    nextStatus: "draft" | "published",
  ): Promise<void> => {
    event.preventDefault();
    setBrandingNotice(null);

    if (nextStatus === "published" && !isPro) {
      setBrandingNotice({
        tone: "warning",
        message: storefrontErrorLabel("pro_plan_required"),
      });
      return;
    }

    try {
      await setStorefront.mutateAsync({
        slug: slug.trim(),
        branding: currentBranding,
        status: nextStatus,
      });
      setBrandingNotice({
        tone: "success",
        message:
          nextStatus === "published"
            ? "Vitrine publicada. Ela já está acessível na URL pública."
            : "Rascunho salvo. Publique quando quiser deixá-la pública.",
      });
    } catch (error) {
      setBrandingNotice({
        tone: "danger",
        message: error instanceof Error ? storefrontErrorLabel(error.message) : "Não foi possível salvar agora.",
      });
    }
  };

  const handleAddItem = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setItemNotice(null);

    if (!linkId) {
      setItemNotice({ tone: "danger", message: "Selecione um link para adicionar." });
      return;
    }

    const priceCents = parsePriceToCents(itemPrice);
    if (priceCents === "invalid") {
      setItemNotice({ tone: "danger", message: "Preço inválido. Use apenas números, ex: 199,90." });
      return;
    }

    try {
      await upsertItem.mutateAsync({
        linkId,
        title: itemTitle.trim(),
        imageUrl: itemImageUrl.trim() || null,
        priceCents,
        isVisible: true,
      });
      setItemNotice({ tone: "success", message: "Item adicionado à vitrine." });
      setLinkId("");
      setItemTitle("");
      setItemImageUrl("");
      setItemPrice("");
    } catch (error) {
      setItemNotice({
        tone: "danger",
        message: error instanceof Error ? storefrontErrorLabel(error.message) : "Não foi possível adicionar o item.",
      });
    }
  };

  const handleToggleVisible = async (item: StorefrontItem): Promise<void> => {
    setBusyItemId(item.id);
    setItemNotice(null);
    try {
      await upsertItem.mutateAsync({
        itemId: item.id,
        linkId: item.linkId,
        title: item.title,
        imageUrl: item.imageUrl,
        priceCents: item.priceCents,
        isVisible: !item.isVisible,
      });
    } catch (error) {
      setItemNotice({
        tone: "danger",
        message: error instanceof Error ? storefrontErrorLabel(error.message) : "Não foi possível atualizar o item.",
      });
    } finally {
      setBusyItemId(null);
    }
  };

  const handleRemove = async (item: StorefrontItem): Promise<void> => {
    setBusyItemId(item.id);
    setItemNotice(null);
    try {
      await deleteItem.mutateAsync(item.id);
    } catch (error) {
      setItemNotice({
        tone: "danger",
        message: error instanceof Error ? storefrontErrorLabel(error.message) : "Não foi possível remover o item.",
      });
    } finally {
      setBusyItemId(null);
    }
  };

  const handleDragEnd = (event: DragEndEvent): void => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = orderedIds.indexOf(String(active.id));
    const newIndex = orderedIds.indexOf(String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;

    const nextOrder = arrayMove(orderedIds, oldIndex, newIndex);
    setOrderedIds(nextOrder);
    reorderItems.mutate(nextOrder);
  };

  const publicUrl = slug.trim() ? `${STOREFRONT_PUBLIC_BASE}/${slug.trim()}` : null;

  return (
    <div className="max-w-6xl space-y-6">
      <header className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="space-y-1">
          <h1 className="text-2xl font-bold tracking-tight text-foreground">Vitrine</h1>
          <p className="text-sm text-muted-foreground">
            Monte uma página pública com seus links de afiliado, no formato vitrine ou bio.
          </p>
        </div>

        {storefront && (
          <div className="flex items-center gap-2">
            <StatusPill published={isPublished} />
            {isPublished && publicUrl && (
              <Button asChild size="sm" variant="outline">
                <a href={publicUrl} target="_blank" rel="noreferrer">
                  Ver vitrine
                  <ExternalLink size={14} />
                </a>
              </Button>
            )}
          </div>
        )}
      </header>

      {storefrontError && (
        <InlineNotice tone="danger">Erro ao carregar a vitrine: {storefrontError.message}</InlineNotice>
      )}

      {loadingStorefront && (
        <div className="h-40 animate-pulse rounded-xl border border-border bg-[hsl(var(--surface-raised)/0.35)]" />
      )}

      {!loadingStorefront && (
        <div className="grid gap-6 xl:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]">
          <div className="min-w-0 space-y-6">
            <Card variant="raised" className="overflow-hidden">
              <CardHeader className="border-b border-border bg-[hsl(var(--surface-raised)/0.45)]">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <Store size={18} />
                  </div>
                  <div className="space-y-1">
                    <CardTitle>Identidade da vitrine</CardTitle>
                    <CardDescription>
                      Endereço público, marca e layout. O endereço usa letras, números e hífens.
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="space-y-4 pt-5">
                <form
                  className="space-y-4"
                  onSubmit={(event) => handleSaveBranding(event, isPublished ? "published" : "draft")}
                >
                  <div className="space-y-2">
                    <Label htmlFor="vitrine-slug" variant="field">
                      Endereço público
                    </Label>
                    <div className="flex items-center gap-2">
                      <span className="shrink-0 text-xs text-muted-foreground">loja.lucralink.com.br/</span>
                      <Input
                        id="vitrine-slug"
                        value={slug}
                        onChange={(event) => setSlug(event.target.value)}
                        placeholder="minha-loja"
                        autoComplete="off"
                        spellCheck={false}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="vitrine-name" variant="field">
                      Nome da loja
                    </Label>
                    <Input
                      id="vitrine-name"
                      value={storeName}
                      onChange={(event) => setStoreName(event.target.value)}
                      placeholder="Achados da Ana"
                      autoComplete="off"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="vitrine-bio" variant="field">
                      Bio
                    </Label>
                    <Input
                      id="vitrine-bio"
                      value={bio}
                      onChange={(event) => setBio(event.target.value)}
                      placeholder="Os melhores achados com link de afiliado."
                      autoComplete="off"
                    />
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="vitrine-logo" variant="field">
                        URL do logo
                      </Label>
                      <Input
                        id="vitrine-logo"
                        type="url"
                        value={logoUrl}
                        onChange={(event) => setLogoUrl(event.target.value)}
                        placeholder="https://..."
                        autoComplete="off"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="vitrine-accent" variant="field">
                        Cor de destaque
                      </Label>
                      <div className="flex items-center gap-2">
                        <input
                          id="vitrine-accent"
                          type="color"
                          value={accentColor}
                          onChange={(event) => setAccentColor(event.target.value)}
                          className="h-9 w-12 shrink-0 cursor-pointer rounded-md border border-border bg-transparent"
                          aria-label="Cor de destaque"
                        />
                        <Input
                          value={accentColor}
                          onChange={(event) => setAccentColor(event.target.value)}
                          placeholder="#1F8A4C"
                          autoComplete="off"
                          spellCheck={false}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label variant="field">Layout</Label>
                    <div className="grid grid-cols-2 gap-2">
                      {STOREFRONT_LAYOUTS.map((option) => (
                        <button
                          key={option}
                          type="button"
                          onClick={() => setLayout(option)}
                          className={cn(
                            "rounded-lg border px-3 py-2.5 text-left text-sm font-medium transition-colors",
                            layout === option
                              ? "border-primary/45 bg-primary/10 text-foreground"
                              : "border-border bg-[hsl(var(--surface)/0.7)] text-muted-foreground hover:border-primary/30",
                          )}
                          aria-pressed={layout === option}
                        >
                          {option === "grid" ? "Vitrine (grade)" : "Bio (lista)"}
                        </button>
                      ))}
                    </div>
                  </div>

                  {brandingNotice && <InlineNotice tone={brandingNotice.tone}>{brandingNotice.message}</InlineNotice>}

                  <div className="flex flex-col gap-2 sm:flex-row">
                    <Button type="submit" variant="secondary" disabled={setStorefront.isPending}>
                      {setStorefront.isPending ? <Loader2 size={15} className="animate-spin" /> : null}
                      Salvar rascunho
                    </Button>

                    <PublishButton
                      isPro={isPro}
                      isPublished={isPublished}
                      isPending={setStorefront.isPending}
                      onPublish={(event) => handleSaveBranding(event, "published")}
                      onUnpublish={(event) => handleSaveBranding(event, "draft")}
                    />
                  </div>

                  {!isPro && (
                    <p className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Lock size={12} />
                      Publicar a vitrine faz parte do plano Pro. Salvar rascunho é liberado em qualquer plano.
                    </p>
                  )}
                </form>
              </CardContent>
            </Card>

            <VitrineSettingsCard isPro={isPro} publicUrl={isPublished ? publicUrl : null} />

            <Card className="overflow-hidden">
              <CardHeader className="border-b border-border bg-[hsl(var(--surface-raised)/0.45)]">
                <CardTitle>Itens da vitrine</CardTitle>
                <CardDescription>
                  Cada item aponta para um link de afiliado já criado. Arraste para reordenar.
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-4 pt-5">
                <form className="space-y-3 rounded-lg border border-border bg-[hsl(var(--surface)/0.75)] p-3" onSubmit={handleAddItem}>
                  <div className="space-y-2">
                    <Label htmlFor="item-link" variant="field">
                      Link de afiliado
                    </Label>
                    <select
                      id="item-link"
                      value={linkId}
                      onChange={(event) => setLinkId(event.target.value)}
                      className="flex h-9 w-full rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--input))] px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <option value="">Selecione um link...</option>
                      {availableLinks.map((link) => (
                        <option key={link.id} value={link.id}>
                          {marketplaceLabel(link.marketplace_code)} - {truncate(link.original_url, 48)}
                        </option>
                      ))}
                    </select>
                    {availableLinks.length === 0 && (
                      <p className="text-xs text-muted-foreground">
                        Todos os seus links já estão na vitrine. Gere novos links na página Links.
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="item-title" variant="field">
                      Título
                    </Label>
                    <Input
                      id="item-title"
                      value={itemTitle}
                      onChange={(event) => setItemTitle(event.target.value)}
                      placeholder="Fone Bluetooth XYZ"
                      autoComplete="off"
                    />
                  </div>

                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="item-image" variant="field">
                        URL da imagem
                      </Label>
                      <Input
                        id="item-image"
                        type="url"
                        value={itemImageUrl}
                        onChange={(event) => setItemImageUrl(event.target.value)}
                        placeholder="https://..."
                        autoComplete="off"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="item-price" variant="field">
                        Preço (R$)
                      </Label>
                      <Input
                        id="item-price"
                        inputMode="decimal"
                        value={itemPrice}
                        onChange={(event) => setItemPrice(event.target.value)}
                        placeholder="199,90"
                        autoComplete="off"
                      />
                    </div>
                  </div>

                  {itemNotice && <InlineNotice tone={itemNotice.tone}>{itemNotice.message}</InlineNotice>}

                  <Button type="submit" disabled={upsertItem.isPending || !linkId}>
                    {upsertItem.isPending ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />}
                    Adicionar item
                  </Button>
                </form>

                {loadingItems ? (
                  <div className="space-y-2">
                    {Array.from({ length: 2 }).map((_, index) => (
                      <div key={index} className="h-16 animate-pulse rounded-lg border border-border bg-background/60" />
                    ))}
                  </div>
                ) : orderedItems.length === 0 ? (
                  <p className="rounded-lg border border-dashed border-border bg-background/40 px-4 py-6 text-center text-sm text-muted-foreground">
                    Nenhum item ainda. Adicione um link acima para começar.
                  </p>
                ) : (
                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext items={orderedIds} strategy={verticalListSortingStrategy}>
                      <div className="space-y-2">
                        {orderedItems.map((item) => (
                          <StorefrontItemRow
                            key={item.id}
                            item={item}
                            onToggleVisible={(target) => void handleToggleVisible(target)}
                            onRemove={(target) => void handleRemove(target)}
                            isBusy={busyItemId === item.id}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="space-y-3 xl:sticky xl:top-6 xl:self-start">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">
              <Eye size={13} />
              Pré-visualização
            </div>
            <StorefrontPreview branding={currentBranding} items={orderedItems} />
          </div>
        </div>
      )}
    </div>
  );
}

interface PublishButtonProps {
  isPro: boolean;
  isPublished: boolean;
  isPending: boolean;
  onPublish: (event: FormEvent<HTMLFormElement>) => void;
  onUnpublish: (event: FormEvent<HTMLFormElement>) => void;
}

function PublishButton({ isPro, isPublished, isPending, onPublish, onUnpublish }: PublishButtonProps): JSX.Element {
  if (isPublished) {
    return (
      <Button
        type="button"
        variant="outline"
        disabled={isPending}
        onClick={(event) => onUnpublish(event as unknown as FormEvent<HTMLFormElement>)}
      >
        Despublicar
      </Button>
    );
  }

  return (
    <Button
      type="button"
      variant="gold"
      disabled={isPending || !isPro}
      title={!isPro ? "Disponível no plano Pro" : undefined}
      onClick={(event) => onPublish(event as unknown as FormEvent<HTMLFormElement>)}
    >
      {!isPro && <Lock size={14} />}
      Publicar vitrine
    </Button>
  );
}

function StatusPill({ published }: { published: boolean }): JSX.Element {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium",
        published
          ? "border-primary/25 bg-primary/10 text-primary"
          : "border-border bg-[hsl(var(--surface-raised))] text-muted-foreground",
      )}
    >
      <span className={cn("h-1.5 w-1.5 rounded-full", published ? "bg-primary" : "bg-muted-foreground")} />
      {published ? "Publicada" : "Rascunho"}
    </span>
  );
}

function InlineNotice({ children, tone }: { children: ReactNode; tone: NoticeTone }): JSX.Element {
  const toneClasses: Record<NoticeTone, string> = {
    neutral: "border-border bg-[hsl(var(--surface)/0.75)] text-muted-foreground",
    success: "border-primary/25 bg-primary/10 text-primary",
    warning: "border-[hsl(var(--gold)/0.25)] bg-[hsl(var(--gold)/0.10)] text-[hsl(var(--gold))]",
    danger: "border-destructive/30 bg-destructive/10 text-destructive",
  };

  return (
    <div className={cn("flex items-start gap-3 rounded-lg border px-4 py-3 text-sm", toneClasses[tone])}>
      {tone === "success" ? (
        <CheckCircle2 size={16} className="mt-0.5 shrink-0" />
      ) : (
        <AlertCircle size={16} className="mt-0.5 shrink-0" />
      )}
      <span>{children}</span>
    </div>
  );
}

function buildBranding(input: {
  storeName: string;
  bio: string;
  logoUrl: string;
  accentColor: string;
  layout: StorefrontLayout;
}): StorefrontBranding {
  const branding: StorefrontBranding = { layout: input.layout };

  const storeName = input.storeName.trim();
  if (storeName) branding.storeName = storeName;

  const bio = input.bio.trim();
  if (bio) branding.bio = bio;

  const logoUrl = input.logoUrl.trim();
  if (logoUrl) branding.logoUrl = logoUrl;

  const accentColor = input.accentColor.trim();
  if (accentColor) branding.accentColor = accentColor;

  return branding;
}

/** Converte texto de preco (ex: "199,90") para centavos. "invalid" em erro. */
function parsePriceToCents(value: string): number | null | "invalid" {
  const trimmed = value.trim();
  if (!trimmed) return null;

  const normalized = trimmed.replace(/\s/g, "").replace(/\./g, "").replace(",", ".");
  if (!/^\d+(\.\d{1,2})?$/.test(normalized)) return "invalid";

  const reais = Number(normalized);
  if (!Number.isFinite(reais) || reais < 0) return "invalid";

  return Math.round(reais * 100);
}

function truncate(value: string, max: number): string {
  return value.length > max ? `${value.slice(0, max)}...` : value;
}
